import { format } from 'date-fns-tz';
import {action, computed, observable} from 'mobx';
import { observer } from 'mobx-react';
import {
    addDurationToDate,
    autoinject,
    bind,
    CommonAppStore,
    componentReaction,
    displayFormatTime,
    Duration,
    inject,
    ReactSlider,
    SocketIcon,
    SubTopBar,
    Tooltip,
    toTimezoneAndFormat,
} from 'Mri.Web.Common';
import * as React from 'react';
import { AppStore } from '../../../Core/AppStore';
import { LiveChartsSelector } from './LiveChartsSelector';
import { LiveChartsTimeSpanSelector } from './LiveChartsTimeSpanSelector';
import { LiveChartsGridSelector } from './LiveChartsGridSelector';
import { DisplayListViewModel } from '../../../DisplayFilters/DisplayListViewModel';
import { BsExclamationTriangle } from '@react-icons/all-files/bs/BsExclamationTriangle';
import {Input, InputGroup, InputGroupText} from "reactstrap";

export interface ILiveChartsTopBarProps {
    onStartTimeChange(startTime: Date | undefined): void;
    onChartNameClick?(chartName: string): void;
    onTimeWindowChange(): void;
    readonly singleChart?: boolean;
    readonly serialNumber?: string;
    readonly vm?: DisplayListViewModel;
    readonly startTime: Date;
    readonly endTime: Date;
    readonly maxValue: Date;
    readonly liveRefresh: boolean;
    readonly timeWindow: Duration;
    readonly chartNames: string[];
    readonly sliderMin: number;
    readonly hasMoreDataThanWindow: boolean;
}

@autoinject
@observer
export class LiveChartsTopBar extends React.Component<ILiveChartsTopBarProps> {
    public render() {
        return (
            <SubTopBar>
                <LiveChartsSelector onChartNameClick={this.props.onChartNameClick} chartNames={this.props.chartNames} />

                <LiveChartsTimeSpanSelector
                    onTimeWindowChange={this.props.onTimeWindowChange}
                    maxValue={this.props.maxValue}
                    timeWindow={this.props.timeWindow}
                />

               <div className="ms-2" style={{width:'350px'}}>
                   <InputGroup size="sm">
                       <InputGroupText className="btn-primary">
                           Sec/Sample
                       </InputGroupText>
                       <Input placeholder="Sec/Sample" 
                              value={this.appStore.storage.liveChartSettings.secsPerSample} 
                              onChange={this.updateSecsPerSample} 
                              onKeyDown={this.triggerSecPerSampleTrackDataUpdate}
                              style={{ minWidth: '0px', maxWidth: '50px' }} 
                       />
                   </InputGroup>
               </div>   

                <div className="text-center w-100 px-2">
                    {this.props.startTime && this.props.endTime && (
                        <div className="text-center">
                            {this.props.serialNumber && (
                                <Tooltip message="Uses the timezone of the first display">
                                    <i>
                                        {displayFormatTime(this.props.startTime, 'MMM d, yyyy, h:mm a', this.props.serialNumber)} to{' '}
                                        {displayFormatTime(this.props.endTime, 'MMM d, yyyy h:mm a z', this.props.serialNumber)}
                                    </i>
                                </Tooltip>
                            )}
                            {!this.props.serialNumber && (
                                <i>
                                    {format(this.props.startTime, 'h:mm a')} to{' '}
                                    {format(this.props.endTime, 'h:mm a zzzz')}
                                </i>
                            )}

                            {/* @TODO refactor to allow toggling live refresh *}
                            {/*{this.props.liveRefresh && (*/}
                            {/*    <SocketIcon*/}
                            {/*        connected={this.props.liveRefresh}*/}
                            {/*        className="ms-2"*/}
                            {/*        connectedText="Refreshing"*/}
                            {/*    />*/}
                            {/*)}*/}
                            {this.error && (
                                <Tooltip
                                    message={
                                        typeof this.error == 'object'
                                            ? this.error.message ?? this.error.Message
                                            : this.error
                                    }
                                >
                                    <BsExclamationTriangle className=" text-danger ms-2" />
                                </Tooltip>
                            )}
                        </div>
                    )}

                    <ReactSlider
                        min={this.props.sliderMin}
                        max={this.props.maxValue.valueOf()}
                        steps={this.steps}
                        initialValue={this.props.startTime.valueOf()}
                        formatTooltip={this.formatTooltip}
                        onChange={this.onDateChange}
                        tooltipPlacement={this.appStore.hiddenHeader ? 'bottom' : 'top'}
                        className="w-100 p-2 m-0"
                    />
                </div>

                {!this.props.singleChart && (
                    <>
                        <LiveChartsGridSelector />

                        {this.commonAppStore.chartHasDataOutOfBounds && (
                            <div className="d-flex">
                                {this.commonAppStore.liveChartStaticScaling && (
                                    <button className="btn btn-sm btn-warning ms-2" onClick={this.toggleStaticScaling}>
                                        Data Out Of Bounds
                                    </button>
                                )}

                                {!this.commonAppStore.liveChartStaticScaling && (
                                    <button className="btn btn-sm btn-primary ms-2" onClick={this.toggleStaticScaling}>
                                        Dynamically scaled
                                    </button>
                                )}
                            </div>
                        )}

                        {this.appStore.storage.liveChartSettings.liveChartHideLegend && (
                            <button
                                className="btn btn-sm btn-primary ms-2"
                                onClick={e => this.onLegendToggle(e, false)}
                            >
                                Show Legend
                            </button>
                        )}

                        {!this.appStore.storage.liveChartSettings.liveChartHideLegend && (
                            <button className="btn btn-sm btn-primary ms-2" onClick={e => this.onLegendToggle(e, true)}>
                                Hide Legend
                            </button>
                        )}

                        {this.appStore.hiddenHeader && (
                            <button className="btn btn-sm btn-primary ms-2" onClick={this.appStore.showHeader}>
                                Show Header
                            </button>
                        )}

                        {!this.appStore.hiddenHeader && (
                            <button className="btn btn-sm btn-primary ms-2" onClick={this.appStore.hideHeader}>
                                Hide Header
                            </button>
                        )}
                    </>
                )}
            </SubTopBar>
        );
    }
    
    @bind
    @action
    private updateSecsPerSample(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.appStore.storage.liveChartSettings.secsPerSample = parseInt(target.value) || 0;
    }
    
    @bind
    @action
    private triggerSecPerSampleTrackDataUpdate(event: React.KeyboardEvent){
        if(event.key=="Enter") {
            this.appStore.storage.liveChartSettings.triggerSecsPerSampleUpdate = !this.appStore.storage.liveChartSettings.triggerSecsPerSampleUpdate;
        }
    }

    @bind
    @action
    private async toggleStaticScaling(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        this.commonAppStore.liveChartStaticScaling = !this.commonAppStore.liveChartStaticScaling;
    }

    @bind
    @action
    private onLegendToggle(event: React.MouseEvent<HTMLButtonElement>, v: boolean) {
        event.preventDefault();

        this.appStore.storage.liveChartSettings.liveChartHideLegend = v;
    }

    @bind
    @action
    private onDateChange(v: number) {
        this.props.onStartTimeChange(new Date(v));
    }

    @bind
    private formatTooltip(v: number) {
        if (this.props.serialNumber) {
            return (
                displayFormatTime(new Date(v), 'MMM d, yyyy, h:mm a', this.props.serialNumber) +
                ' to ' +
                displayFormatTime(
                    addDurationToDate(new Date(v), this.props.timeWindow),
                    'h:mm a',
                    this.props.serialNumber
                )
            );
        }

        return (
            format(new Date(v), 'h:mm a') +
            ' to ' +
            format(addDurationToDate(new Date(v), this.props.timeWindow), 'h:mm a')
        );
    }
    
    @computed
    private get steps(){
        let sps = this.appStore.storage.liveChartSettings.secsPerSample;
        if(sps > 200) {
            return sps / 8 * 1000;
        }
        if(sps > 100) {
            return sps / 2 * 1000;
        }
        return sps * 1000;
    }

    @observable
    private error: any;
    @inject
    private appStore!: AppStore;
    @inject
    private commonAppStore!: CommonAppStore;
}
